import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';

import type { ImportRowDto } from '../../lib/api/endpoints';

interface PreviewRow {
  id: string;
  symbol: string;
  date: string;
  side: string;
  qty: string;
  price: string;
  currency: string;
  netCash: string;
  errorMessage: string | null;
  status: 'NEW' | 'DUPLICATE' | 'ERROR';
}

interface ImportPreviewTableProps {
  rows: ImportRowDto[];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function previewRow(row: ImportRowDto): PreviewRow {
  const parsed = row.parsedJson;
  const date = text(parsed.tradeDate ?? parsed.asOfDate).slice(0, 10);
  return {
    id: row.id,
    symbol: text(parsed.symbol),
    date,
    side: text(parsed.side),
    qty: text(parsed.quantity),
    price: text(parsed.price),
    currency: text(parsed.currency),
    netCash: text(parsed.netCash ?? parsed.amount),
    errorMessage: row.errorMessage,
    status: row.errorMessage ? 'ERROR' : row.isDuplicate ? 'DUPLICATE' : 'NEW',
  };
}

function statusClass(status: PreviewRow['status']): string {
  if (status === 'ERROR') return 'bg-danger/10 text-danger';
  if (status === 'DUPLICATE') return 'bg-warn/10 text-warn';
  return 'bg-success/10 text-success';
}

const columns: Array<ColumnDef<PreviewRow>> = [
  { accessorKey: 'symbol', header: 'Symbol' },
  { accessorKey: 'date', header: 'Date' },
  { accessorKey: 'side', header: 'Side' },
  { accessorKey: 'qty', header: 'Qty' },
  { accessorKey: 'price', header: 'Price' },
  { accessorKey: 'currency', header: 'Currency' },
  { accessorKey: 'netCash', header: '$ Net' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`rounded px-2 py-1 text-xs font-medium ${statusClass(row.original.status)}`}
        title={row.original.errorMessage ?? row.original.status}
      >
        {row.original.status}
      </span>
    ),
  },
  {
    accessorKey: 'errorMessage',
    header: 'Error',
    cell: ({ row }) =>
      row.original.errorMessage ? (
        <span className="block max-w-xs text-danger" title={row.original.errorMessage}>
          {row.original.errorMessage}
        </span>
      ) : (
        <span className="text-text-muted">-</span>
      ),
  },
];

export function ImportPreviewTable({ rows }: ImportPreviewTableProps) {
  const data = rows.slice(0, 100).map(previewRow);
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="border-b border-border" key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th className="px-3 py-2 font-medium text-text-muted" key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr className="border-b border-border last:border-0" key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td className="px-3 py-2 tabular-nums" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 sm:hidden">
        {data.map((row) => (
          <div className="rounded border border-border bg-bg p-3" key={row.id}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">{row.symbol || 'Statement row'}</p>
                <p className="text-sm text-text-muted">{row.date}</p>
              </div>
              <span className={`rounded px-2 py-1 text-xs font-medium ${statusClass(row.status)}`}>
                {row.status}
              </span>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-sm tabular-nums">
              <div>
                <dt className="text-text-muted">Side</dt>
                <dd>{row.side || '-'}</dd>
              </div>
              <div>
                <dt className="text-text-muted">Qty</dt>
                <dd>{row.qty || '-'}</dd>
              </div>
              <div>
                <dt className="text-text-muted">Price</dt>
                <dd>{row.price || '-'}</dd>
              </div>
              <div>
                <dt className="text-text-muted">Currency</dt>
                <dd>{row.currency || '-'}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-text-muted">$ Net</dt>
                <dd>{row.netCash || '-'}</dd>
              </div>
              {row.errorMessage ? (
                <div className="col-span-2">
                  <dt className="text-text-muted">Error</dt>
                  <dd className="text-danger">{row.errorMessage}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
